import { useQuery } from "@tanstack/react-query";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Loader2 } from "lucide-react";

interface DayStatus {
  date: string;
  status: 'success' | 'failed' | 'skipped' | 'pruned' | 'none';
  detail?: string;
  skippedReason?: string;
  prunedAt?: string;
  creatorName?: string;
}

interface BackupHistoryData {
  projectId: string;
  days: number;
  html: DayStatus[];
  database: DayStatus[];
  zip: DayStatus[];
  generatedAt: string;
}

interface BackupHistoryTimelineProps {
  projectId: string;
}

export function BackupHistoryTimeline({ projectId }: BackupHistoryTimelineProps) {
  const { data, isLoading, error } = useQuery<BackupHistoryData>({
    queryKey: [`/api/projects/${projectId}/backup-history?days=60`],
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-2" data-testid="backup-history-loading">
        <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
        <span className="ml-2 text-xs text-muted-foreground">Loading...</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-xs text-muted-foreground py-2" data-testid="backup-history-error">
        Unable to load backup history
      </div>
    );
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  };

  // Get visual color class for each status
  // Success: Solid green, Pruned: Hollow green, Skipped: Hollow yellow, Failed: Solid red, None: Grey
  const getStatusColor = (status: DayStatus['status']) => {
    switch (status) {
      case 'success':
        return 'bg-emerald-500';
      case 'pruned':
        return 'bg-transparent border border-emerald-500/60';
      case 'skipped':
        return 'bg-transparent border border-yellow-500/60';
      case 'failed':
        return 'bg-red-500';
      default:
        return 'bg-slate-700/50';
    }
  };

  const getStatusLabel = (day: DayStatus) => {
    switch (day.status) {
      case 'success':
        return 'Success';
      case 'pruned':
        return 'Pruned (file deleted)';
      case 'skipped':
        return day.skippedReason === 'no_changes' ? 'Skipped (no changes)' : 'Skipped';
      case 'failed':
        return 'Failed';
      default:
        return 'No backup';
    }
  };

  const getStatusTextColor = (status: DayStatus['status']) => {
    switch (status) {
      case 'success':
        return 'text-emerald-400';
      case 'pruned':
        return 'text-emerald-400/70';
      case 'skipped':
        return 'text-yellow-400';
      case 'failed':
        return 'text-red-400';
      default:
        return 'text-muted-foreground';
    }
  };

  const renderRow = (days: DayStatus[], rowType: 'html' | 'database' | 'zip', label: string) => (
    <div className="flex items-center gap-1">
      <span className="text-[10px] font-medium text-muted-foreground w-8 shrink-0">
        {label}
      </span>
      <div className="flex gap-px">
        {days.slice(0, 60).map((day, idx) => (
          <Tooltip key={day.date}>
            <TooltipTrigger asChild>
              <div
                className={`w-1.5 h-3 rounded-[1px] cursor-pointer transition-all hover:scale-y-125 ${getStatusColor(day.status)}`}
                data-testid={`backup-block-${rowType}-${idx}`}
              />
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              <div className="font-medium">{formatDate(day.date)}</div>
              <div className={getStatusTextColor(day.status)}>
                {getStatusLabel(day)}
              </div>
              {day.creatorName && (
                <div className="text-muted-foreground text-[10px] mt-0.5">
                  Creator: {day.creatorName}
                </div>
              )}
              {day.detail && (
                <div className="text-muted-foreground text-[10px] max-w-[200px] truncate mt-0.5">
                  {day.detail}
                </div>
              )}
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-1.5" data-testid="backup-history-timeline">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <span className="text-xs font-medium text-foreground">Backup History</span>
          <span className="text-[10px] text-muted-foreground">(60 days)</span>
        </div>
        <div className="flex items-center gap-2 text-[9px] text-muted-foreground">
          <span>Today</span>
          <span>→</span>
          <span>-60d</span>
        </div>
      </div>
      
      <div className="flex flex-col gap-0.5 p-2 bg-slate-900/50 rounded border border-slate-800">
        {renderRow(data.html, 'html', 'HTML')}
        {renderRow(data.database, 'database', 'DB')}
        {renderRow(data.zip, 'zip', 'ZIP')}
      </div>
      
      <div className="flex items-center gap-2 text-[9px] text-muted-foreground flex-wrap">
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-2 rounded-[1px] bg-emerald-500" />
          <span>Success</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-2 rounded-[1px] bg-transparent border border-emerald-500/60" />
          <span>Pruned</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-2 rounded-[1px] bg-transparent border border-yellow-500/60" />
          <span>Skipped</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-2 rounded-[1px] bg-red-500" />
          <span>Failed</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-2 rounded-[1px] bg-slate-700/50" />
          <span>None</span>
        </div>
      </div>
    </div>
  );
}
